/* eslint-disable sonarjs/no-duplicate-string */
import React, { useEffect, useRef } from 'react'
import ContentfulImage from 'experiences/shared/ContentfulImage'
import { FacebookShareButton, LinkedinShareButton, TwitterShareButton } from 'react-share'
import { truncArray } from 'lib'
import { FacebookIcon, LinkedInIcon, LinkIcon, TwitterIcon } from 'components/patterns/icons'
import { COLORS } from 'parameters/index'
import AnalyticsService from 'services/AnalyticsLite/AnalyticsService'
import { Breadcrumbs } from 'components/elements/Breadcrumbs'
import {
	Anchor,
	ArticleBody,
	ArticleContainer,
	ArticleContent,
	ArticleImage,
	ArticleInfo,
	ArticleSocial,
	ArticleSocialIcons,
	ArticleTitle,
	Author,
	Date,
	ProgressBar,
	Tooltip
} from './ArticleDetail.style'
import { Article } from 'services/ContentfulService/models/articles'
import { TopicsSection } from 'experiences/learn/topics/TopicsSection'
import { Topic } from 'services/ContentfulService/models/topics'
import { PrioritizedContent } from 'experiences/shared/ContentfulImage/PriorityContext'
import { FavoriteButton } from 'experiences/learn/articles/FavoriteButton/FavoriteButton'
import { SingleResource } from 'services/ContentfulService/models/resource/resource'
import { useAuthState } from 'services/Authentication'
import { RichText } from 'components/vendor/Contentful/richText/RichTextRenderer'
import { ResourceRenderer } from 'components/vendor/Contentful/richText/ResourceRenderer'
import { ClientOnlyRender } from 'lib/renderWrappers/ClientOnly'

interface Props {
	article: Article
	disclaimer: SingleResource
	featuredTopics: Topic[]
}

export const ArticlePage = ({ article, disclaimer, featuredTopics }: Props): JSX.Element => {
	const { userType } = useAuthState()
	const progressBarRef = useRef<HTMLDivElement>(null)
	const articleContentRef = useRef<HTMLDivElement>(null)

	const handleScroll = (): void => {
		const progressBarElement = progressBarRef.current
		const articleElement = articleContentRef.current

		if (progressBarElement && articleElement) {
			const targetHeight = articleElement.offsetHeight + articleElement.offsetTop - window.innerHeight

			window.scrollY > targetHeight
				? (progressBarElement.style.width = '100%')
				: (progressBarElement.style.width = (window.scrollY / targetHeight) * 100 + '%')
		}
	}

	useEffect(() => {
		window.addEventListener('scroll', handleScroll)

		return (): void => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	const copyUrlToClipboard = (): void => {
		navigator.clipboard.writeText(window.location.href)
		document.getElementById('tooltip')!.innerHTML = 'Copied!'
	}

	const outFunc = (): void => {
		document.getElementById('tooltip')!.innerHTML = 'Copy to clipboard'
	}

	return (
		<div>
			<ProgressBar ref={progressBarRef} />
			<ArticleContainer>
				<Breadcrumbs
					breadcrumbs={[
						{ name: 'Learn', url: '/learn' },
						{ name: 'Articles', url: '/learn/articles' }
					]}
				/>
				<ArticleTitle>{article.title}</ArticleTitle>
				<ArticleImage>
					<PrioritizedContent>
						<ContentfulImage
							asset={article.primaryImage}
							// placeholder={true}
							height={300}
							quality={60}
							objectPosition="0 20%"
							sizes="(min-width: 1366px) 800px, (min-width: 723px) 60vw, 40vw"
							layout="fill"
							objectFit={'cover'}
						/>
					</PrioritizedContent>
				</ArticleImage>
				<ArticleContent>
					<ArticleInfo>
						<Author>{`${article.author.firstName} ${article.author.lastName}`}</Author>
						<Date>
							{article.dateWritten}
							{/*{article.dateWritten} · {`${calculateReadingTime(documentToHtmlString(article.content))} min read`}*/}
						</Date>
					</ArticleInfo>
					<ArticleBody>
						<RichText document={article.content} />
					</ArticleBody>
					<ArticleSocial>
						<ArticleSocialIcons>
							<ClientOnlyRender>
								<Anchor>{userType === 'loggedIn' && <FavoriteButton id={article.id} />}</Anchor>
							</ClientOnlyRender>
							<Anchor>
								<TwitterShareButton
									url={typeof window !== 'undefined' ? window.location.href : ''}
									title={article.title}
									onClick={() => AnalyticsService.track('Social Share', { category: 'Article', label: 'Twitter' })}
								>
									<TwitterIcon color={COLORS.SPANISH_GRAY} />
								</TwitterShareButton>
							</Anchor>
							<Anchor>
								<FacebookShareButton
									url={typeof window !== 'undefined' ? window.location.href : ''}
									quote={article.title}
									onClick={() => AnalyticsService.track('Social Share', { category: 'Article', label: 'Facebook' })}
								>
									<FacebookIcon color={COLORS.SPANISH_GRAY} />
								</FacebookShareButton>
							</Anchor>
							<Anchor>
								<LinkedinShareButton
									url={typeof window !== 'undefined' ? window.location.href : ''}
									title={article.title}
									onClick={() => AnalyticsService.track('Social Share', { category: 'Article', label: 'LinkedIn' })}
								>
									<LinkedInIcon color={COLORS.SPANISH_GRAY} />
								</LinkedinShareButton>
							</Anchor>
							<Anchor onClick={copyUrlToClipboard} onMouseLeave={outFunc}>
								<Tooltip>
									<span className="tooltiptext" id="tooltip">
										Copy to clipboard
									</span>
									<LinkIcon color={COLORS.SPANISH_GRAY} />
								</Tooltip>
							</Anchor>
						</ArticleSocialIcons>
					</ArticleSocial>
				</ArticleContent>
			</ArticleContainer>
			<ArticleContainer>
				<TopicsSection
					topics={truncArray(featuredTopics, 4)}
					title={'You might also be interested in'}
					linkTo={'/learn/topics'}
				/>
			</ArticleContainer>
			<ArticleContainer style={{ marginTop: '64px' }}>
				<ResourceRenderer resource={disclaimer} />
			</ArticleContainer>
		</div>
	)
}
